import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { useParams } from "react-router-dom";
import { erpApi } from "../../../api/erp";
import { ErpEmpty, ErpLoading, ErpPanel, ErpSectionTitle } from "../../../components/dashboard/erp/ErpUi";
import { ERP_BRAND, PAYMENT_METHOD_LABELS, TRANSACTION_KIND_LABELS, entityId, formatDateTime, formatMoney } from "../../../utils/erp";

const KINDS = ["receipt", "payment", "refund", "transfer", "adjustment", "reversal"];
const METHODS = ["cash", "bank_transfer", "card", "digital_wallet", "check", "other"];

export default function ProjectErpTransactionsPage() {
  const { projectId } = useParams();
  const [transactions, setTransactions] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [filters, setFilters] = React.useState({ kind: "", method: "", dateFrom: "", dateTo: "" });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await erpApi.listTransactions(projectId, { page: page + 1, pageSize: rowsPerPage, ...filters });
      setTransactions(data.transactions || []);
      setTotal(Number(data.total || 0));
    } catch (err) {
      setError(err.message || "No se pudieron cargar las transacciones");
    } finally {
      setLoading(false);
    }
  }, [projectId, page, rowsPerPage, filters]);

  React.useEffect(() => { load(); }, [load]);

  const setFilter = (key, value) => {
    setPage(0);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  return (
    <Stack spacing={2.4}>
      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}
      <ErpSectionTitle title="Transacciones" description="Cobros, pagos, devoluciones y transferencias generadas por movimientos confirmados." action={<Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={load} sx={{ textTransform: "none", fontWeight: 850 }}>Actualizar</Button>} />

      <ErpPanel>
        <Box sx={{ p: 2.2, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.3}>
            <TextField select size="small" label="Tipo" value={filters.kind} onChange={(event) => setFilter("kind", event.target.value)} sx={{ minWidth: 190 }}>
              <MenuItem value="">Todos</MenuItem>
              {KINDS.map((kind) => <MenuItem key={kind} value={kind}>{TRANSACTION_KIND_LABELS[kind]}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Método" value={filters.method} onChange={(event) => setFilter("method", event.target.value)} sx={{ minWidth: 220 }}>
              <MenuItem value="">Todos</MenuItem>
              {METHODS.map((method) => <MenuItem key={method} value={method}>{PAYMENT_METHOD_LABELS[method]}</MenuItem>)}
            </TextField>
            <TextField size="small" type="date" label="Desde" value={filters.dateFrom} onChange={(event) => setFilter("dateFrom", event.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="date" label="Hasta" value={filters.dateTo} onChange={(event) => setFilter("dateTo", event.target.value)} InputLabelProps={{ shrink: true }} />
          </Stack>
        </Box>
        {loading ? <ErpLoading label="Cargando transacciones..." /> : !transactions.length ? <ErpEmpty title="Sin transacciones" description="Los cobros y pagos aparecerán al confirmar movimientos con medios de pago." /> : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Número / fecha</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Método</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell>Destino / origen</TableCell>
                    <TableCell>Caja</TableCell>
                    <TableCell align="right">Importe</TableCell>
                    <TableCell>Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={entityId(transaction)} hover>
                      <TableCell>
                        <Typography sx={{ fontWeight: 900 }}>{transaction.transactionNumber}</Typography>
                        <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{formatDateTime(transaction.occurredAt)}</Typography>
                      </TableCell>
                      <TableCell><Chip size="small" label={TRANSACTION_KIND_LABELS[transaction.kind] || transaction.kind} /></TableCell>
                      <TableCell>{PAYMENT_METHOD_LABELS[transaction.method] || transaction.method}</TableCell>
                      <TableCell>{transaction.description || "—"}</TableCell>
                      <TableCell>{transaction.toAccountCode || transaction.fromAccountCode || "—"}</TableCell>
                      <TableCell>{transaction.cashRegisterId?.name || "—"}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 950 }}>{formatMoney(transaction.amount, transaction.currency)}</TableCell>
                      <TableCell><Chip size="small" color={transaction.status === "posted" ? "success" : "error"} label={transaction.status === "posted" ? "Registrada" : "Anulada"} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={total} page={page} onPageChange={(_event, nextPage) => setPage(nextPage)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }} rowsPerPageOptions={[10, 25, 50, 100]} labelRowsPerPage="Filas por página" />
          </>
        )}
      </ErpPanel>
    </Stack>
  );
}
